import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Boxes,
  Download,
  MapPin,
  MoveHorizontal,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { getError } from "@/api";

const money = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "GNF",
  maximumFractionDigits: 0,
});

const quickZoneOptions = [
  { value: "zone_bureau", label: "Bureau" },
  { value: "zone_conakry", label: "Conakry" },
  { value: "zone_interieur", label: "Intérieur" },
];

const quickTypeOptions = [
  { value: "principal", label: "Principal" },
  { value: "zone_conakry", label: "Zone Conakry" },
  { value: "zone_interieur", label: "Zone Intérieur" },
  { value: "livreur", label: "Livreur" },
];

const stockAlertLabel = (row) => (
  row.alert_type === "delivery_stock_limit"
    ? "Dépôt livreur : plus de 10 unités"
    : "Sous seuil"
);

export default function StockPage({ quickLookup = false, user }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [debouncedProductSearch, setDebouncedProductSearch] = useState("");
  const [warehouseSearch, setWarehouseSearch] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");
  const [subzoneFilter, setSubzoneFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [selectedZones, setSelectedZones] = useState([]);
  const [selectedWarehouseTypes, setSelectedWarehouseTypes] = useState([]);
  const [alertsOnly, setAlertsOnly] = useState(
    new URLSearchParams(window.location.search).get("alertes") === "1",
  );
  const [error, setError] = useState("");
  const [isProductSearchLoading, setIsProductSearchLoading] = useState(false);
  const latestInventoryRequest = useRef(0);
  const inventoryAbortController = useRef(null);

  const load = useCallback((searchTerm = debouncedProductSearch) => {
    const requestId = latestInventoryRequest.current + 1;
    latestInventoryRequest.current = requestId;
    inventoryAbortController.current?.abort();
    const controller = new AbortController();
    inventoryAbortController.current = controller;
    setIsProductSearchLoading(true);

    return api.get("/inventory", {
      params: { search: searchTerm },
      signal: controller.signal,
    })
      .then((response) => {
        if (requestId !== latestInventoryRequest.current) {
          return;
        }
        setRows(response.data);
        setError("");
      })
      .catch((requestError) => {
        if (requestId === latestInventoryRequest.current) {
          setError(getError(requestError));
        }
      })
      .finally(() => {
        if (requestId === latestInventoryRequest.current) {
          setIsProductSearchLoading(false);
        }
      });
  }, [debouncedProductSearch]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedProductSearch(productSearch.trim());
    }, 180);

    return () => window.clearTimeout(timer);
  }, [productSearch]);

  useEffect(() => {
    api.get("/warehouses")
      .then((response) => setWarehouses(response.data))
      .catch((requestError) => setError(getError(requestError)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const zones = useMemo(() => {
    return [...new Set(warehouses.map((warehouse) => warehouse.zone_id).filter(Boolean))].sort();
  }, [warehouses]);
  const subzones = useMemo(() => {
    return [...new Set(
      warehouses
        .filter((warehouse) => !zoneFilter || warehouse.zone_id === zoneFilter)
        .map((warehouse) => warehouse.subzone)
        .filter(Boolean),
    )].sort();
  }, [warehouses, zoneFilter]);
  const warehouseTypes = useMemo(() => {
    return [...new Set(warehouses.map((warehouse) => warehouse.warehouse_type).filter(Boolean))].sort();
  }, [warehouses]);
  const filteredWarehouses = useMemo(() => {
    const normalizedSearch = warehouseSearch.toLocaleLowerCase("fr-FR");

    return warehouses.filter((warehouse) => {
      const searchText = [warehouse.name, warehouse.zone_id, warehouse.subzone, warehouse.warehouse_type]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("fr-FR");
      const matchesZone = !zoneFilter || warehouse.zone_id === zoneFilter;
      const matchesSubzone = !subzoneFilter || warehouse.subzone === subzoneFilter;
      const matchesType = !typeFilter || warehouse.warehouse_type === typeFilter;
      const matchesSelectedZones = !selectedZones.length
        || selectedZones.includes(warehouse.zone_id);
      const matchesSelectedTypes = !selectedWarehouseTypes.length
        || selectedWarehouseTypes.includes(warehouse.warehouse_type);

      return searchText.includes(normalizedSearch)
        && matchesZone
        && matchesSubzone
        && matchesType
        && matchesSelectedZones
        && matchesSelectedTypes;
    });
  }, [
    selectedWarehouseTypes,
    selectedZones,
    subzoneFilter,
    typeFilter,
    warehouseSearch,
    warehouses,
    zoneFilter,
  ]);
  const warehousesById = useMemo(() => {
    return Object.fromEntries(warehouses.map((warehouse) => [warehouse.warehouse_id, warehouse]));
  }, [warehouses]);
  const filteredWarehouseIds = useMemo(() => {
    return new Set(filteredWarehouses.map((warehouse) => warehouse.warehouse_id));
  }, [filteredWarehouses]);
  const hasWarehouseCriteria = Boolean(
    warehouseSearch
    || zoneFilter
    || subzoneFilter
    || typeFilter
    || selectedZones.length
    || selectedWarehouseTypes.length,
  );
  const displayed = useMemo(() => {
    return rows.filter((row) => {
      const matchesSelectedWarehouses = !selectedWarehouseIds.length
        || selectedWarehouseIds.includes(row.warehouse_id);
      const matchesWarehouseCriteria = !hasWarehouseCriteria
        || filteredWarehouseIds.has(row.warehouse_id);

      return matchesSelectedWarehouses && matchesWarehouseCriteria && (!alertsOnly || row.is_alert);
    });
  }, [
    alertsOnly,
    filteredWarehouseIds,
    hasWarehouseCriteria,
    rows,
    selectedWarehouseIds,
  ]);
  const productAvailability = useMemo(() => {
    const grouped = new Map();

    displayed.forEach((row) => {
      const productId = row.product_id || row.product?.product_id;
      const existing = grouped.get(productId) || {
        productId,
        productName: row.product?.name || "Produit",
        totalQuantity: 0,
        warehouses: [],
      };
      existing.totalQuantity += Number(row.quantity || 0);
      existing.warehouses.push({
        inventoryId: row.inventory_id,
        warehouseName: warehousesById[row.warehouse_id]?.name || "Dépôt",
        quantity: row.quantity,
      });
      grouped.set(productId, existing);
    });

    return [...grouped.values()].sort((left, right) => (
      left.productName.localeCompare(right.productName, "fr")
    ));
  }, [displayed, warehousesById]);
  const availabilityTotalQuantity = useMemo(() => {
    return productAvailability.reduce((total, product) => total + product.totalQuantity, 0);
  }, [productAvailability]);
  const availabilityScopeLabel = useMemo(() => {
    if (!hasWarehouseCriteria && !selectedWarehouseIds.length) {
      return "Tous les dépôts";
    }

    return `${filteredWarehouses.length} dépôt(s) dans le périmètre`;
  }, [filteredWarehouses.length, hasWarehouseCriteria, selectedWarehouseIds.length]);
  const canPrepareDeliveryTransfer = Boolean(
    user?.permissions?.includes("*") || user?.permissions?.includes("movement:manage"),
  );

  const prepareDeliveryTransfer = (row) => {
    const params = new URLSearchParams({
      movement_type: "transfert",
      product_id: row.product_id,
      warehouse_id: row.warehouse_id,
    });

    navigate(`/mouvements?${params.toString()}`);
  };

  const toggleWarehouse = (warehouseId) => {
    setSelectedWarehouseIds((current) => (
      current.includes(warehouseId)
        ? current.filter((item) => item !== warehouseId)
        : [...current, warehouseId]
    ));
  };

  const toggleQuickFilter = (value, setter) => {
    setter((current) => (
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    ));
  };

  const clearWarehouseFilters = () => {
    setWarehouseSearch("");
    setZoneFilter("");
    setSubzoneFilter("");
    setTypeFilter("");
    setSelectedZones([]);
    setSelectedWarehouseTypes([]);
    setSelectedWarehouseIds([]);
  };

  const isProductSearchPending = productSearch.trim() !== debouncedProductSearch;
  const availabilitySummary = productSearch.trim() && !isProductSearchLoading && !isProductSearchPending ? (
    <section
      aria-live="polite"
      className="stock-availability-summary"
      data-testid="stock-product-availability-summary"
    >
      <div className="stock-availability-summary-heading">
        <div>
          <p className="eyebrow">RÉSULTAT DE RECHERCHE</p>
          <h2 data-testid="stock-product-availability-title">
            Disponibilité pour « {productSearch.trim()} »
          </h2>
          <p className="stock-availability-scope" data-testid="stock-availability-scope-label">
            <MapPin aria-hidden="true" size={14} />
            {availabilityScopeLabel}
          </p>
        </div>
        <div className="stock-availability-summary-metrics">
          <span data-testid="stock-product-availability-product-count">
            {productAvailability.length} produit(s)
          </span>
          <strong data-testid="stock-product-availability-total-quantity">
            <Boxes aria-hidden="true" size={17} />
            {availabilityTotalQuantity} unité(s)
          </strong>
        </div>
      </div>
      <div className="stock-availability-products">
        {productAvailability.map((product) => (
          <article
            data-testid={`stock-product-availability-${product.productId}`}
            key={product.productId}
          >
            <header>
              <div>
                <span>PRODUIT</span>
                <strong data-testid={`stock-product-availability-name-${product.productId}`}>
                  {product.productName}
                </strong>
              </div>
              <div className="stock-product-total">
                <span>Total disponible</span>
                <b data-testid={`stock-product-availability-total-${product.productId}`}>
                  {product.totalQuantity}
                </b>
              </div>
            </header>
            <div>
              {product.warehouses.map((warehouse) => (
                <span
                  data-testid={`stock-product-depot-${warehouse.inventoryId}`}
                  key={warehouse.inventoryId}
                >
                  <span>
                    <small>Dépôt</small>
                    <strong>{warehouse.warehouseName}</strong>
                  </span>
                  <span className="stock-availability-quantity">
                    <small>Disponible</small>
                    <b>{warehouse.quantity}</b>
                  </span>
                </span>
              ))}
            </div>
          </article>
        ))}
        {!isProductSearchLoading && !productAvailability.length && (
          <p data-testid="empty-stock-product-search-message">
            Aucune quantité disponible ne correspond à ce produit et à ces filtres.
          </p>
        )}
      </div>
    </section>
  ) : null;
  const warehouseFilterPanel = (
    <section className="stock-warehouse-filter-panel" data-testid="stock-warehouse-filter-panel">
      <div className="stock-warehouse-filter-heading">
        <div>
          <p className="eyebrow">PÉRIMÈTRE DES RÉSULTATS</p>
          <h2>{filteredWarehouses.length} dépôt(s) retenu(s)</h2>
        </div>
        <button
          className="text-action"
          data-testid="clear-stock-warehouse-filters-button"
          onClick={clearWarehouseFilters}
          type="button"
        >
          <X size={15} /> Réinitialiser
        </button>
      </div>
      <div className="warehouse-filter-controls">
        <div className="filter-search">
          <Search aria-hidden="true" size={17} />
          <input
            data-testid="stock-warehouse-quick-search-input"
            onChange={(event) => setWarehouseSearch(event.target.value)}
            placeholder="Nom, zone ou type de dépôt"
            value={warehouseSearch}
          />
        </div>
        <select
          data-testid="stock-warehouse-zone-filter"
          onChange={(event) => {
            setZoneFilter(event.target.value);
            setSubzoneFilter("");
          }}
          value={zoneFilter}
        >
          <option value="">Toutes les zones</option>
          {zones.map((zone) => <option key={zone} value={zone}>{zone}</option>)}
        </select>
        <select
          data-testid="stock-warehouse-subzone-filter"
          onChange={(event) => setSubzoneFilter(event.target.value)}
          value={subzoneFilter}
        >
          <option value="">Toutes les sous-zones</option>
          {subzones.map((subzone) => <option key={subzone} value={subzone}>{subzone}</option>)}
        </select>
        <select
          data-testid="stock-warehouse-type-filter"
          onChange={(event) => setTypeFilter(event.target.value)}
          value={typeFilter}
        >
          <option value="">Tous les types</option>
          {warehouseTypes.map((type) => <option key={type} value={type}>{type}</option>)}
        </select>
      </div>
      <div
        className="warehouse-quick-checkbox-filters"
        data-testid="stock-warehouse-quick-checkbox-filters"
      >
        <fieldset data-testid="stock-zone-checkbox-filter-group">
          <legend>Filtrer par zone</legend>
          <div>
            {quickZoneOptions.map((option) => (
              <label key={option.value}>
                <input
                  checked={selectedZones.includes(option.value)}
                  data-testid={`stock-zone-checkbox-${option.value}`}
                  onChange={() => toggleQuickFilter(option.value, setSelectedZones)}
                  type="checkbox"
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset data-testid="stock-type-checkbox-filter-group">
          <legend>Filtrer par type de dépôt</legend>
          <div>
            {quickTypeOptions.map((option) => (
              <label key={option.value}>
                <input
                  checked={selectedWarehouseTypes.includes(option.value)}
                  data-testid={`stock-type-checkbox-${option.value}`}
                  onChange={() => toggleQuickFilter(option.value, setSelectedWarehouseTypes)}
                  type="checkbox"
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      </div>
      <div className="warehouse-checkbox-list" data-testid="stock-warehouse-checkbox-list">
        {filteredWarehouses.map((warehouse) => (
          <label
            data-testid={`stock-warehouse-option-${warehouse.warehouse_id}`}
            key={warehouse.warehouse_id}
          >
            <input
              checked={selectedWarehouseIds.includes(warehouse.warehouse_id)}
              data-testid={`stock-warehouse-checkbox-${warehouse.warehouse_id}`}
              onChange={() => toggleWarehouse(warehouse.warehouse_id)}
              type="checkbox"
            />
            <span>
              <strong>{warehouse.name}</strong>
              <small>{warehouse.zone_id} · {warehouse.subzone} · {warehouse.warehouse_type}</small>
            </span>
          </label>
        ))}
        {!filteredWarehouses.length && (
          <p data-testid="empty-stock-warehouse-filter-message">
            Aucun dépôt ne correspond aux filtres.
          </p>
        )}
      </div>
    </section>
  );

  return (
    <div className="page-enter" data-testid="stock-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">{quickLookup ? "CONSULTATION DE STOCK" : "STOCK DISPONIBLE"}</p>
          <h1 data-testid="stock-title">{quickLookup ? "Stock Disponible" : "Niveau par dépôt"}</h1>
          <p data-testid="stock-description">
            Recherchez un produit et consultez sa quantité disponible dans chaque dépôt.
          </p>
        </div>
        {!quickLookup && (
          <a
            className="secondary-action"
            data-testid="export-products-button"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/export/products.csv`}
          >
            <Download size={17} /> Exporter CSV
          </a>
        )}
      </div>
      <div className="filters-bar" data-testid="stock-filters">
        <div className="filter-search">
          <Search aria-hidden="true" size={17} />
          <input
            data-testid="stock-product-search-input"
            onChange={(event) => setProductSearch(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && load(productSearch.trim())}
            placeholder="Rechercher un produit"
            value={productSearch}
          />
          {(isProductSearchLoading || isProductSearchPending) && (
            <span className="stock-search-loading" data-testid="stock-product-search-loading">
              Recherche…
            </span>
          )}
        </div>
        <button
          className={`filter-toggle ${alertsOnly ? "filter-toggle-active" : ""}`}
          data-testid="alerts-only-filter-button"
          onClick={() => setAlertsOnly((current) => !current)}
          type="button"
        >
          <SlidersHorizontal size={16} /> Alertes uniquement
        </button>
      </div>
      {productSearch.trim() ? (
        <div className="stock-lookup-workspace" data-testid="stock-lookup-workspace">
          {availabilitySummary}
          {warehouseFilterPanel}
        </div>
      ) : (
        warehouseFilterPanel
      )}
      {error && <div className="notice-error" data-testid="stock-error-message">{error}</div>}
      <section className="data-panel stock-table-panel" data-testid="stock-table-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">INVENTAIRE</p>
            <h2>{displayed.length} ligne(s) de stock</h2>
          </div>
          {alertsOnly && (
            <span className="alert-pill" data-testid="stock-alert-filter-status">
              <AlertTriangle size={15} /> Seuils à traiter
            </span>
          )}
        </div>
        <div className="responsive-table">
          <table data-testid="stock-table">
            <thead>
              <tr>
                <th>Produit</th>
                <th>Dépôt</th>
                <th className="number">Disponible</th>
                <th className="number">Seuil</th>
                <th className="number">Valorisation</th>
                <th>État</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((row) => (
                <tr
                  className={
                    row.alert_type === "delivery_stock_limit"
                      ? "delivery-stock-alert-row"
                      : row.is_alert
                        ? "alert-row"
                        : ""
                  }
                  data-testid={`stock-row-${row.inventory_id}`}
                  key={row.inventory_id}
                >
                  <td><strong data-testid={`stock-product-${row.inventory_id}`}>{row.product.name}</strong></td>
                  <td data-testid={`stock-warehouse-${row.inventory_id}`}>
                    {warehousesById[row.warehouse_id]?.name || "Dépôt"}
                  </td>
                  <td className="number" data-testid={`stock-quantity-${row.inventory_id}`}>{row.quantity}</td>
                  <td className="number" data-testid={`stock-threshold-${row.inventory_id}`}>{row.threshold}</td>
                  <td className="number" data-testid={`stock-value-${row.inventory_id}`}>
                    {money.format(row.quantity * row.sale_price)}
                  </td>
                  <td>
                    {row.is_alert ? (
                      <div className="stock-alert-actions">
                        <span
                          className={
                            row.alert_type === "delivery_stock_limit"
                              ? "delivery-stock-alert-pill"
                              : "alert-pill"
                          }
                          data-testid={`stock-alert-${row.inventory_id}`}
                        >
                          {stockAlertLabel(row)}
                        </span>
                        {row.alert_type === "delivery_stock_limit"
                          && canPrepareDeliveryTransfer && (
                            <button
                              className="stock-delivery-transfer-button"
                              data-testid={`prepare-delivery-transfer-${row.inventory_id}`}
                              onClick={() => prepareDeliveryTransfer(row)}
                              type="button"
                            >
                              <MoveHorizontal aria-hidden="true" size={14} />
                              Préparer transfert
                            </button>
                          )}
                      </div>
                    ) : (
                      <span className="ok-pill" data-testid={`stock-ok-${row.inventory_id}`}>Normal</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!rows.length && (
          <div className="empty-inline" data-testid="empty-stock-message">
            Aucun produit en stock. Enregistrez une entrée pour commencer.
          </div>
        )}
      </section>
    </div>
  );
}